import { useEffect, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { Modal, ConfirmationModal, FormField, TextInput, Spacer } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useLoginCaptcha } from '@percent/cause-dashboard/common/hooks/useAuthCaptcha/useAuthCaptcha'

interface ReauthenticationModalProps {
  open: boolean
  email?: string
  onSuccess: () => void
  onCancel: () => void
}

type Step = 'password' | '2fa'

const REQUIRED_2FA_LOGIN = 'auth/requires_two_fa_login'

export function ReauthenticationModal({ open, email, onSuccess, onCancel }: Readonly<ReauthenticationModalProps>) {
  const { t } = useTranslation()
  const { authService } = useServices()
  const [errorMessage, setErrorMessage] = useState('')
  const [step, setStep] = useState<Step>('password')
  const getCaptchaHeaders = useLoginCaptcha(open && step === 'password')

  const passwordForm = useFormik({
    initialValues: {
      password: ''
    },
    validationSchema: () =>
      yup.object().shape({
        password: yup.string().min(8).max(64).required('Required')
      }),
    onSubmit: async ({ password }) => {
      try {
        setErrorMessage('')
        await authService.login({ email: email ?? '', password, enforce: true }, await getCaptchaHeaders())
        onSuccess()
      } catch (error: any) {
        if (error?.response?.status === 400 && error?.response?.data?.error?.code === REQUIRED_2FA_LOGIN) {
          setStep('2fa')
          return
        }
        setErrorMessage(t('reauthentication.error'))
      }
    }
  })

  const twoFaForm = useFormik({
    initialValues: {
      token: ''
    },
    validationSchema: () =>
      yup.object().shape({
        token: yup
          .string()
          .required('Required')
          .matches(/^[0-9]+$/, t('errorMessage.onlyDigits'))
          .min(6, t('errorMessage.max6Digit'))
          .max(6, t('errorMessage.max6Digit'))
      }),
    onSubmit: async ({ token }) => {
      try {
        setErrorMessage('')
        await authService.verifyOtpKey({ token })
        onSuccess()
      } catch {
        setErrorMessage(t('reauthentication.twoFaError'))
      }
    }
  })

  useEffect(() => {
    if (open) {
      passwordForm.resetForm()
      twoFaForm.resetForm()
      setErrorMessage('')
      setStep('password')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const isPasswordStep = step === 'password'
  const activeForm = isPasswordStep ? passwordForm : twoFaForm

  return (
    <Modal open={open} onClose={onCancel} data-testid="reauthentication-modal">
      <ConfirmationModal
        title={isPasswordStep ? t('reauthentication.title') : t('reauthentication.twoFaTitle')}
        type="submit"
        loading={activeForm.isSubmitting}
        disabled={!(activeForm.isValid && activeForm.dirty)}
        primaryButtonText={isPasswordStep ? t('reauthentication.submit') : t('reauthentication.twoFaSubmit')}
        primaryBtnTestId="reauthentication-submit"
        secondaryButtonText={t('reauthentication.cancel')}
        secondaryBtnTestId="reauthentication-cancel"
        handleClose={onCancel}
        handleSubmit={activeForm.handleSubmit}
      >
        <Spacer size={4} />
        {isPasswordStep ? (
          <form onSubmit={passwordForm.handleSubmit}>
            <FormikProvider value={passwordForm}>
              <p>{t('reauthentication.description')}</p>
              {errorMessage && <p data-testid="reauthentication-error">{errorMessage}</p>}
              <FormField
                label={t('reauthentication.passwordLabel')}
                status={passwordForm.touched.password && passwordForm.errors.password ? 'danger' : 'default'}
                statusMessage={passwordForm.errors.password}
                necessity="required"
              >
                <TextInput
                  type="password"
                  name="password"
                  placeholder={t('reauthentication.passwordPlaceholder')}
                  onChange={passwordForm.handleChange}
                  onBlur={passwordForm.handleBlur}
                  value={passwordForm.values.password}
                />
              </FormField>
            </FormikProvider>
          </form>
        ) : (
          <form onSubmit={twoFaForm.handleSubmit}>
            <FormikProvider value={twoFaForm}>
              <p>{t('reauthentication.twoFaDescription')}</p>
              {errorMessage && <p data-testid="reauthentication-error">{errorMessage}</p>}
              <FormField
                label={t('reauthentication.tokenLabel')}
                status={twoFaForm.touched.token && twoFaForm.errors.token ? 'danger' : 'default'}
                statusMessage={twoFaForm.errors.token}
                necessity="required"
              >
                <TextInput
                  name="token"
                  placeholder={t('reauthentication.tokenPlaceholder')}
                  onChange={twoFaForm.handleChange}
                  onBlur={twoFaForm.handleBlur}
                  value={twoFaForm.values.token}
                  data-testid="reauthentication-token"
                />
              </FormField>
            </FormikProvider>
          </form>
        )}
      </ConfirmationModal>
    </Modal>
  )
}
